/**
 * Typisierte Hooks für `/api/v1/properties`, `/buildings` und `/units` —
 * dieselbe Domäne wie im Backend (`domains/properties`, Spec 02a, „Bestand":
 * „Gebäude und Einheiten liegen in derselben Domäne").
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`). Query-Keys kommen ausschließlich aus
 * `lib/query-keys.ts`; jede Mutation invalidiert über ein Präfix, nie über
 * einzeln aufgezählte Keys.
 *
 * `usePropertiesList` ist mit Spec 02a entfallen: Der Baum holt seine Daten
 * jetzt von `GET /entities/tree` (`features/objects/api.ts`). Der Endpunkt
 * `GET /properties` selbst bleibt — er trägt weiterhin `?updated_since=`
 * (`ARCHITECTURE.md` 5, I10) und ist die Grundlage bestehender API-Tests.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request, SessionChangedError } from '@/lib/api'
import { createPendingEntityId } from '@/lib/optimistic-entities'
import { keys } from '@/lib/query-keys'

export type PropertyDetail = components['schemas']['PropertyDetail']
export type PropertyCreate = components['schemas']['PropertyCreate']
export type PropertyUpdate = components['schemas']['PropertyUpdate']
export type BuildingDetail = components['schemas']['BuildingDetail']
export type BuildingCreate = components['schemas']['BuildingCreate']
export type BuildingUpdate = components['schemas']['BuildingUpdate']
export type UnitDetail = components['schemas']['UnitDetail']
export type UnitCreate = components['schemas']['UnitCreate']
export type UnitUpdate = components['schemas']['UnitUpdate']
export type UsageOption = components['schemas']['LabeledValue']
export type UsageTypeValue = components['schemas']['UsageType']
/** Antwort von `GET /entities/{id}/archive-preview` — entities-Domäne, aber
 * nur hier gebraucht (`<Art>MasterData`, alle drei in `features/properties`):
 * Ein eigenes, kleines `request()` statt eines Imports aus
 * `features/objects` (`apps/web/CLAUDE.md`, „features kennen einander
 * nicht"). */
export type ArchivePreview = components['schemas']['ArchivePreview']

interface QueryOptions {
  readonly enabled?: boolean
}

// ── Optimistische Zeile beim Anlegen (Spec 02b, „Schnellerfassung") ──────
//
// Nur beim Weg über „und noch eins" (`optimistic: true` in den Mutations-
// Argumenten unten) — der primäre Speichern-Knopf ruft dieselbe Mutation
// ohne dieses Flag auf und bleibt synchron. Grund laut Spec: Eine
// zurückgenommene Zeile braucht einen Ort für ihre Fehlermeldung, den gibt
// es nur, solange das Sheet offen ist.
//
// `TreeNode`/die Seitenform stehen hier noch einmal lokal, nicht importiert
// aus `features/objects/api.ts` — beide Module leiten sie unabhängig aus
// `lib/api-types.ts` ab, kein Import zwischen den `features` nötig
// (`apps/web/CLAUDE.md`, „features kennen einander nicht").

type EntityTreeNode = components['schemas']['TreeNode']

interface EntityTreePage {
  readonly items: EntityTreeNode[]
  readonly next_cursor: string | null
}

interface OptimisticEntityContext {
  readonly queryKey: readonly unknown[]
  readonly previous: EntityTreePage | undefined
}

/** Schnappschuss der Baumebene ziehen und die neue Zeile anhängen —
 * gemeinsam für Grundstück, Gebäude und Einheit, damit die Regel „nur beim
 * Weg über und noch eins" nicht dreimal geschrieben steht.
 *
 * Schreibt nur, wenn der Zielschlüssel gerade **aktiv beobachtet** wird —
 * also der Baumknoten aufgeklappt ist. Ohne aktiven Beobachter gibt es
 * ohnehin keine sichtbare Zeile, in die optimistisch geschrieben werden
 * könnte, und `onSuccess` unten invalidiert nur breit (per Präfix):
 * `invalidateQueries` holt eine **inaktive** Abfrage dabei nicht neu
 * (`Query.isActive()` prüft `observer.options.enabled`) — die Platzhalterzeile
 * bliebe sonst als Geisterzeile stehen, bis der Knoten zum ersten Mal
 * aufgeklappt wird, und verdeckte dort den echten, unverfälschten
 * Ladezustand. */
async function snapshotAndInsertPending(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  pendingNode: EntityTreeNode,
): Promise<OptimisticEntityContext | undefined> {
  const isTargetActive = queryClient.getQueryCache().find<EntityTreePage>({ queryKey })?.isActive() ?? false
  if (!isTargetActive) return undefined

  await queryClient.cancelQueries({ queryKey })
  const previous = queryClient.getQueryData<EntityTreePage>(queryKey)
  queryClient.setQueryData<EntityTreePage>(queryKey, (current) => ({
    items: [...(current?.items ?? []), pendingNode],
    next_cursor: current?.next_cursor ?? null,
  }))
  return { queryKey, previous }
}

/** Nimmt die optimistische Zeile zurück — die Antwort auf einen
 * gescheiterten Aufruf. Ohne Kontext (synchroner Weg über den primären
 * Knopf) ist nichts zu tun. */
export function rollbackPending(
  queryClient: QueryClient,
  context: OptimisticEntityContext | undefined,
  error: unknown,
): void {
  if (context === undefined || error instanceof SessionChangedError) return
  queryClient.setQueryData(context.queryKey, context.previous)
}

export function usePropertyDetail(propertyId: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.properties.detail(propertyId),
    queryFn: ({ signal }) => request<PropertyDetail>(`/properties/${propertyId}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

interface CreatePropertyArgs {
  readonly body: PropertyCreate
  /** Nur beim Weg über „und noch eins" — siehe Kommentar oben. */
  readonly optimistic?: boolean
}

/**
 * Ein frischer Schlüssel je Aufruf, nicht je Sheet-Sitzung: Zwei
 * `mutate()`-Aufrufe mit demselben Schlüssel, aber unterschiedlichem Rumpf
 * (z. B. nach einer korrigierten Straße) ergäben sonst 409
 * `idempotency_key_reused` statt der erwarteten Fehleranzeige am Feld.
 */
export function useCreateProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body }: CreatePropertyArgs) =>
      request<PropertyDetail>('/properties', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onMutate: ({ body, optimistic }) => {
      if (optimistic !== true) return undefined
      return snapshotAndInsertPending(queryClient, keys.entities.list({ parentId: null }), {
        id: createPendingEntityId(),
        label: body.street,
        kind: { value: 'property', label: 'Grundstück' },
        parent_id: null,
        archived_at: null,
        has_children: false,
        updated_at: new Date().toISOString(),
        version: 0,
        // Ein neu angelegtes Objekt ist nie privat — `visibility` kam mit
        // Spec 05b additiv auf `TreeNode` dazu und ist Pflicht, damit der
        // Baum das Schlosssymbol zeigen kann (`ObjectTree.tsx`).
        visibility: { value: 'inherited', label: 'Geerbt' },
      })
    },
    onError: (error, _variables, context) => {
      rollbackPending(queryClient, context, error)
    },
    onSuccess: () => {
      // Zwei Präfixe: `properties` für die eigene Detailansicht, `entities`
      // für den Baum — seit Spec 02a liest der Baum `GET /entities/tree`,
      // nicht mehr `GET /properties` (Spec 02a, „Was diese Spec an Feature 01
      // anfasst").
      void queryClient.invalidateQueries({ queryKey: keys.properties.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface UpdatePropertyArgs {
  readonly propertyId: string
  readonly body: PropertyUpdate
  readonly ifMatch: string
}

export function useUpdateProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ propertyId, body, ifMatch }: UpdatePropertyArgs) =>
      request<PropertyDetail>(`/properties/${propertyId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      // Zwei Präfixe: `properties` für die eigene Detailansicht, `entities`
      // für den Baum — seit Spec 02a liest der Baum `GET /entities/tree`,
      // nicht mehr `GET /properties` (Spec 02a, „Was diese Spec an Feature 01
      // anfasst").
      void queryClient.invalidateQueries({ queryKey: keys.properties.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface ArchivePropertyArgs {
  readonly propertyId: string
  readonly ifMatch: string
}

export function useArchiveProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ propertyId, ifMatch }: ArchivePropertyArgs) =>
      request<PropertyDetail>(`/properties/${propertyId}/archive`, {
        method: 'POST',
        ifMatch,
      }),
    onSuccess: () => {
      // Zwei Präfixe: `properties` für die eigene Detailansicht, `entities`
      // für den Baum — seit Spec 02a liest der Baum `GET /entities/tree`,
      // nicht mehr `GET /properties` (Spec 02a, „Was diese Spec an Feature 01
      // anfasst").
      void queryClient.invalidateQueries({ queryKey: keys.properties.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

export function useUnarchiveProperty() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ propertyId, ifMatch }: ArchivePropertyArgs) =>
      request<PropertyDetail>(`/properties/${propertyId}/unarchive`, {
        method: 'POST',
        ifMatch,
      }),
    onSuccess: () => {
      // Zwei Präfixe: `properties` für die eigene Detailansicht, `entities`
      // für den Baum — seit Spec 02a liest der Baum `GET /entities/tree`,
      // nicht mehr `GET /properties` (Spec 02a, „Was diese Spec an Feature 01
      // anfasst").
      void queryClient.invalidateQueries({ queryKey: keys.properties.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

// ── Gebäude ──────────────────────────────────────────────────────────────

export function useBuildingDetail(buildingId: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.buildings.detail(buildingId),
    queryFn: ({ signal }) => request<BuildingDetail>(`/buildings/${buildingId}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

interface CreateBuildingArgs {
  readonly body: BuildingCreate
  /** Nur beim Weg über „und noch eins" — siehe Kommentar oben. */
  readonly optimistic?: boolean
}

export function useCreateBuilding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body }: CreateBuildingArgs) =>
      request<BuildingDetail>('/buildings', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onMutate: ({ body, optimistic }) => {
      if (optimistic !== true) return undefined
      return snapshotAndInsertPending(queryClient, keys.entities.list({ parentId: body.parent_id }), {
        id: createPendingEntityId(),
        label: body.label,
        kind: { value: 'building', label: 'Gebäude' },
        parent_id: body.parent_id,
        archived_at: null,
        has_children: false,
        updated_at: new Date().toISOString(),
        version: 0,
        // Siehe `useCreateProperty` oben.
        visibility: { value: 'inherited', label: 'Geerbt' },
      })
    },
    onError: (error, _variables, context) => {
      rollbackPending(queryClient, context, error)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.buildings.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface UpdateBuildingArgs {
  readonly buildingId: string
  readonly body: BuildingUpdate
  readonly ifMatch: string
}

export function useUpdateBuilding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ buildingId, body, ifMatch }: UpdateBuildingArgs) =>
      request<BuildingDetail>(`/buildings/${buildingId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.buildings.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface ArchiveBuildingArgs {
  readonly buildingId: string
  readonly ifMatch: string
}

export function useArchiveBuilding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ buildingId, ifMatch }: ArchiveBuildingArgs) =>
      request<BuildingDetail>(`/buildings/${buildingId}/archive`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.buildings.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

export function useUnarchiveBuilding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ buildingId, ifMatch }: ArchiveBuildingArgs) =>
      request<BuildingDetail>(`/buildings/${buildingId}/unarchive`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.buildings.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

// ── Einheiten ────────────────────────────────────────────────────────────

export function useUnitDetail(unitId: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.units.detail(unitId),
    queryFn: ({ signal }) => request<UnitDetail>(`/units/${unitId}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

/** Die Auswahlliste für `usage_type` — der Client pflegt keine eigene
 * Zuordnung von Werten auf deutsche Bezeichnungen (Spec 02a, „Stammdaten"). */
export function useUsageTypes() {
  return useQuery({
    queryKey: keys.units.usageTypes,
    queryFn: ({ signal }) => request<UsageOption[]>('/units/usage-types', { signal }),
    // Ändert sich zur Laufzeit nie — vier feste Werte aus einem
    // Check-Constraint (Spec 02a, „Datenmodell").
    staleTime: Infinity,
  })
}

interface CreateUnitArgs {
  readonly body: UnitCreate
  /** Nur beim Weg über „und noch eins" — siehe Kommentar oben. */
  readonly optimistic?: boolean
}

export function useCreateUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body }: CreateUnitArgs) =>
      request<UnitDetail>('/units', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onMutate: ({ body, optimistic }) => {
      if (optimistic !== true) return undefined
      return snapshotAndInsertPending(queryClient, keys.entities.list({ parentId: body.parent_id }), {
        id: createPendingEntityId(),
        label: body.unit_ref,
        kind: { value: 'unit', label: 'Einheit' },
        parent_id: body.parent_id,
        archived_at: null,
        has_children: false,
        updated_at: new Date().toISOString(),
        version: 0,
        // Siehe `useCreateProperty` oben.
        visibility: { value: 'inherited', label: 'Geerbt' },
      })
    },
    onError: (error, _variables, context) => {
      rollbackPending(queryClient, context, error)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.units.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface UpdateUnitArgs {
  readonly unitId: string
  readonly body: UnitUpdate
  readonly ifMatch: string
}

export function useUpdateUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ unitId, body, ifMatch }: UpdateUnitArgs) =>
      request<UnitDetail>(`/units/${unitId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.units.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface ArchiveUnitArgs {
  readonly unitId: string
  readonly ifMatch: string
}

export function useArchiveUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ unitId, ifMatch }: ArchiveUnitArgs) =>
      request<UnitDetail>(`/units/${unitId}/archive`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.units.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

export function useUnarchiveUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ unitId, ifMatch }: ArchiveUnitArgs) =>
      request<UnitDetail>(`/units/${unitId}/unarchive`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.units.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

// ── Archivierungsrückfrage (alle drei Arten) ────────────────────────────

/**
 * `GET /entities/{id}/archive-preview` — geladen, bevor die Rückfrage
 * erscheint (Spec 02a, „Oberfläche · Archivieren"). Als Mutation statt als
 * `useQuery`: Der Aufruf ist ein einmaliger Klick-Trigger, kein Zustand, der
 * gemountet bleiben soll.
 */
export function useArchivePreview() {
  return useMutation({
    mutationFn: (entityId: string) => request<ArchivePreview>(`/entities/${entityId}/archive-preview`),
  })
}
